import React, { Children } from 'react';
import { IonButton } from '@ionic/react';
import { useDeviceInfo } from '@/hooks/useDeviceInfo';
import { FooterLogic } from './logic';

export type SmartCaseButtonProps = React.ComponentProps<typeof IonButton> & {
  /**
   * The label's title case, for ios mode, when the automatic conversion gets
   * it wrong, typically a verb particle mid-label ("Set Up the Rest", not
   * "Set up the Rest").
   */
  titleCase?: string;
};

const hasLetters = (text: string) => /\p{L}/u.test(text);

/**
 * An `IonButton` whose label is written in sentence case and shown in the
 * casing of the current Ionic mode: title case in ios mode ("Set Up Your
 * Profile"), as written in md mode ("Set up your profile"). Use it for every
 * Footer Dock button so the dock's labels follow one convention per platform.
 *
 * Only the text children are recased; elements (an icon, a count) pass
 * through untouched, and text on either side of them is cased as parts of one
 * title.
 */
export const SmartCaseButton: React.FC<SmartCaseButtonProps> = ({
  titleCase,
  children,
  ...buttonProps
}) => {
  const { isIonModeIOS } = useDeviceInfo();
  if (!isIonModeIOS) return <IonButton {...buttonProps}>{children}</IonButton>;
  if (titleCase !== undefined) {
    return <IonButton {...buttonProps}>{titleCase}</IonButton>;
  }

  const parts = Children.toArray(children);
  const textIndexes = parts.flatMap((part, i) =>
    typeof part === 'string' && hasLetters(part) ? [i] : []
  );
  const first = textIndexes[0];
  const last = textIndexes[textIndexes.length - 1];
  const label = parts.map((part, i) =>
    typeof part === 'string' && hasLetters(part)
      ? FooterLogic.toTitleCase(part, { first: i === first, last: i === last })
      : part
  );
  return <IonButton {...buttonProps}>{label}</IonButton>;
};
